// <RoleChip> — pill-shaped chip displaying a canonical role name.
//
// Per DESIGN.md §2 (role colors are identifiers, not status) and §7.2:
//   - Background from `colors.role.<role>`.
//   - Label is the full canonical role name (no short codes, ever).
//   - Unknown roles fall back to a neutral color and emit a console.warn
//     (surface once; don't spam per-render).

import "./RoleChip.css";

export interface RoleChipProps {
  role: string;
  size?: "sm" | "md";
}

const ROLE_COLORS: Readonly<Record<string, string>> = Object.freeze({
  tenant_admin: "#B71C1C",
  site_manager: "#1A73E8",
  site_investigator: "#8E24AA",
  site_viewer: "#546E7A",
  view_only: "#546E7A",
  regional_admin: "#00897B",
  fleet_admin: "#E65100",
  system_auditor: "#5E35B1",
});

const NEUTRAL_FALLBACK = "#64748B";
const WARNED = new Set<string>();

export function RoleChip({ role, size = "md" }: RoleChipProps): JSX.Element {
  // Server emits uppercase role names in grants/sessions; component palette
  // is keyed on lowercase canonical names. Normalize for lookup so
  // FLEET_ADMIN maps to the same color as fleet_admin.
  const lookupKey = role.toLowerCase();
  let background = ROLE_COLORS[lookupKey];
  if (background === undefined) {
    background = NEUTRAL_FALLBACK;
    if (!WARNED.has(role)) {
      WARNED.add(role);
      // eslint-disable-next-line no-console
      console.warn(`[RoleChip] unknown role '${role}' — rendering with neutral color`);
    }
  }
  return (
    <span
      className={`rb-role-chip rb-role-chip--${size}`}
      style={{ backgroundColor: background }}
      aria-label={`Role: ${role}`}
    >
      {role}
    </span>
  );
}
